import { px } from '@/utils/hubScale'
import React from 'react'
import styled from 'styled-components'

import { BackToProjectsLink } from '@features/projects/hub/components/BackToProjectsLink'
import { Heading } from '@features/projects/hub/list/styles'
import { actionBannerForStep } from '@features/projects/hub/components/projectStepLabel'
import { usePermissions } from '@hooks/auth/usePermissions'
import { AccountType } from 'src/utils/enums'
import type { ProjectStepType } from '@autoriz/shared/project'

import {
  composeArchitectName,
  composeSalesmanName,
} from '../services/projectMeta'
import type { BreadcrumbContext } from '../types'

const COLORS = {
  ink100: '#f1f1ef',
  ink200: '#dcdbd6',
  ink400: '#8a8980',
  ink500: '#65645e',
  ink700: '#353430',
  ink900: '#14130f',
  amber: '#f97316',
  green50: '#dcfce7',
  green700: '#166534',
  ink050: '#fafaf8',
}

const Root = styled.div`
  font-family: 'Geist', 'Inter', system-ui, sans-serif;
  margin-bottom: ${px(16)};
`

// ── Title row ──────────────────────────────────────────────────────────
const TitleBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${px(12)} ${px(14)};
  margin: ${px(4)} 0 ${px(14)};
`

const RefBadge = styled.span`
  margin-left: auto;
  font-size: ${px(11.5)};
  color: ${COLORS.ink400};
  font-variant-numeric: tabular-nums;
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  align-self: center;
`

const Title = styled(Heading)`
  margin: 0;
`

const TagRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${px(6)};
`

const Tag = styled.span<{ $variant?: 'neutral' | 'green' }>`
  display: inline-flex;
  align-items: center;
  padding: ${px(3)} ${px(10)};
  border-radius: ${px(999)};
  font-size: ${px(12)};
  font-weight: 600;
  letter-spacing: 0.01em;
  ${(p) =>
    p.$variant === 'green'
      ? `background: ${COLORS.green50}; color: ${COLORS.green700};`
      : `background: ${COLORS.ink100}; color: ${COLORS.ink700};`}
`

// ── Meta band ──────────────────────────────────────────────────────────
// Each entry reads on ONE line — label then value, side by side — rather than
// stacking the two. The band is reference material the commercial glances at,
// not something they work in, so it earns a single line of the page's height.
const MetaBand = styled.div`
  padding: ${px(9)} ${px(16)};
  background: ${COLORS.ink050};
  border: ${px(1)} solid ${COLORS.ink200};
  border-radius: ${px(12)};
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${px(6)} ${px(24)};
`

const MetaItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${px(7)};
  min-width: 0;
`

/**
 * Anything the page wants to sit in the same panel as the meta — the lifecycle
 * stepper, today. One bordered block instead of two stacked ones: the project's
 * identity and where it stands are the same glance, and sharing the chrome
 * saves the gap, the second border, and a second set of padding.
 */
const MetaExtra = styled.div`
  margin-top: ${px(10)};
  padding-top: ${px(10)};
  border-top: ${px(1)} solid ${COLORS.ink200};
`

const MetaLabel = styled.span`
  font-size: ${px(11)};
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.ink400};
`

const MetaValue = styled.span`
  font-size: ${px(13.5)};
  color: ${COLORS.ink900};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: ${px(280)};
`

// ── Helpers ────────────────────────────────────────────────────────────

const PROJECT_TYPE_LABEL: Record<string, string> = {
  VERANDA: 'Véranda',
  PERGOLA: 'Pergola',
  POOL: 'Piscine',
}

const PERMIT_KIND_LABEL: Record<string, string> = {
  DP: 'Déclaration préalable',
  DPMI: 'Déclaration préalable',
  PC: 'Permis de construire',
  PCMI: 'Permis de construire',
  PCM: 'Permis modificatif',
}

const composeClientName = (
  client:
    | { first_name?: string | null; last_name?: string | null }
    | null
    | undefined,
): string => {
  if (!client) return 'Client à renseigner'
  const name = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
  return name || 'Client à renseigner'
}

const composeAddress = (project: {
  street_number?: string | null
  street_name?: string | null
  postal_code?: string | null
  city?: string | null
}): string => {
  const street = [project.street_number, project.street_name]
    .filter(Boolean)
    .join(' ')
  const town = [project.postal_code, project.city].filter(Boolean).join(' ')
  return [street, town].filter(Boolean).join(', ') || 'Adresse à renseigner'
}

const formatSurfaceLabel = (
  surface: number | string | null | undefined,
): string | null => {
  if (surface === null || surface === undefined || surface === '') return null
  const n = typeof surface === 'number' ? surface : Number(surface)
  if (!Number.isFinite(n) || n <= 0) return null
  return `${n} m²`
}

const inferPermitKind = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  if (raw.startsWith('DPMI')) return 'DPMI'
  if (raw.startsWith('DP')) return 'DP'
  if (raw.startsWith('PCMI')) return 'PCMI'
  if (raw.startsWith('PCM')) return 'PCM'
  if (raw.startsWith('PC')) return 'PC'
  return null
}

const composePermitKindLabel = (raw: string | null | undefined): string | null => {
  const kind = inferPermitKind(raw)
  return kind ? (PERMIT_KIND_LABEL[kind] ?? kind) : null
}

// Resolve the project-type-specific surface from the nested entity that
// matches `project_type`. Falls back to project-level `surface` (legacy)
// if the nested entity isn't populated.
const resolveTypeSurface = (project: {
  project_type?: string | null
  surface?: number | string | null
  veranda?: { area?: number | null } | null
  pergola?: { area?: number | null } | null
  pool?: { area?: number | null } | null
}): number | string | null | undefined => {
  switch (project.project_type) {
    case 'VERANDA':
      return project.veranda?.area ?? project.surface
    case 'PERGOLA':
      return project.pergola?.area ?? project.surface
    case 'POOL':
      return project.pool?.area ?? project.surface
    default:
      return project.surface
  }
}

// Derived reference label. `project.id` is a stable monotonic int the
// salesman won't otherwise see; expose it as `Réf. AZ-NNNN` so they
// have an unambiguous handle when calling support.
const composeProjectRef = (id: number | null | undefined): string | null => {
  if (id === null || id === undefined || !Number.isFinite(id)) return null
  return `Réf. AZ-${id.toString().padStart(4, '0')}`
}

// Pure mapper — exposed for direct unit testing.
export const toBreadcrumbContext = (project: {
  id: number
  step: string | null | undefined
  project_type: string | null | undefined
  primary_signatory_client?:
    | { first_name?: string | null; last_name?: string | null }
    | null
  street_number?: string | null
  street_name?: string | null
  postal_code?: string | null
  city?: string | null
  surface?: number | string | null
  planning_file_type?: string | null
}): BreadcrumbContext => ({
  projectId: project.id,
  clientName: composeClientName(project.primary_signatory_client),
  stepLabel: project.step
    ? actionBannerForStep(project.step as ProjectStepType).title
    : 'Étape inconnue',
  typeLabel: PROJECT_TYPE_LABEL[project.project_type ?? ''] ?? 'Projet',
  address: composeAddress(project),
  surfaceLabel: formatSurfaceLabel(project.surface),
  permitKind: inferPermitKind(project.planning_file_type),
})

// Wider input shape for the v2 header — pulls extra fields from the
// nested entities + the architect relation. Keep `toBreadcrumbContext`
// above as the small public mapper used by tests; the v2 layout reads
// the project directly so it can access the type-specific surface.
export type HeaderProject = {
  id: number
  step: string | null | undefined
  project_type: string | null | undefined
  planning_file_type?: string | null
  created?: string | null
  primary_signatory_client?:
    | { first_name?: string | null; last_name?: string | null }
    | null
  street_number?: string | null
  street_name?: string | null
  postal_code?: string | null
  city?: string | null
  surface?: number | string | null
  veranda?: { area?: number | null } | null
  pergola?: { area?: number | null } | null
  pool?: { area?: number | null } | null
  architect?:
    | { first_name?: string | null; last_name?: string | null }
    | null
  salesman?:
    | { first_name?: string | null; last_name?: string | null }
    | null
}

export type BreadcrumbHeaderProps = {
  project: HeaderProject
  /** Rendered inside the meta panel, under a divider. See {@link MetaExtra}. */
  children?: React.ReactNode
}

export const BreadcrumbHeader: React.FC<BreadcrumbHeaderProps> = ({
  project,
  children,
}) => {
  // A company works across its commercials' dossiers, so it needs to know whose
  // this one is — the same reason its project cards carry the owner's name.
  const { data: accountType } = usePermissions<AccountType>()
  const isCompany = accountType === AccountType.SOCIETY

  const clientName = composeClientName(project.primary_signatory_client)
  const ref = composeProjectRef(project.id)
  const permitLabel = composePermitKindLabel(project.planning_file_type)
  const typeLabel = PROJECT_TYPE_LABEL[project.project_type ?? ''] ?? null
  const typeSurface = formatSurfaceLabel(resolveTypeSurface(project))
  const typeTag =
    typeLabel && typeSurface
      ? `${typeLabel} ${typeSurface}`
      : (typeLabel ?? typeSurface)
  const address = composeAddress(project)
  const architectName = composeArchitectName(project.architect)
  const salesmanName = isCompany ? composeSalesmanName(project.salesman) : null

  return (
    <Root data-cy="salesman-hub-header">
      <BackToProjectsLink />

      <TitleBlock>
        <Title data-cy="salesman-hub-header--client">{clientName}</Title>
        <TagRow>
          {permitLabel && (
            <Tag
              $variant="green"
              data-cy="salesman-hub-header--permit-tag"
            >
              {permitLabel}
            </Tag>
          )}
          {typeTag && (
            <Tag data-cy="salesman-hub-header--type-tag">{typeTag}</Tag>
          )}
        </TagRow>
        {ref && (
          <RefBadge data-cy="salesman-hub-header--ref">{ref}</RefBadge>
        )}
      </TitleBlock>

      <MetaBand data-cy="salesman-hub-header--meta">
        <MetaRow>
          {address !== 'Adresse à renseigner' && (
            <MetaItem data-cy="salesman-hub-header--meta-address">
              <MetaLabel>Adresse</MetaLabel>
              <MetaValue title={address}>{address}</MetaValue>
            </MetaItem>
          )}
          {/* #668 — "Créé le" and the architect's initials disc moved to the
              Informations page. Neither is read between two appointments, and
              the disc was the only avatar on the page: pure decoration
              competing with the status band beneath it. The architect's NAME
              stays: it is who the commercial calls. */}
          {salesmanName && (
            <MetaItem data-cy="salesman-hub-header--meta-salesman">
              <MetaLabel>Commercial</MetaLabel>
              <MetaValue>{salesmanName}</MetaValue>
            </MetaItem>
          )}
          {architectName && (
            <MetaItem data-cy="salesman-hub-header--meta-architect">
              <MetaLabel>Architecte</MetaLabel>
              <MetaValue>{architectName}</MetaValue>
            </MetaItem>
          )}
        </MetaRow>
        {children && <MetaExtra>{children}</MetaExtra>}
      </MetaBand>
    </Root>
  )
}
